import React, { useState } from "react";
import "./verticalNavigation.css";
import verticalNavigationBg from "../../assets/verticalNavigation/overlay.png";
import Modal from "../modal/modal.tsx";
import type { ModalType } from "../modal/modal.tsx";
import type { TagState } from "../../data/types";

interface NavigationButton {
  label: string;
  onClick: () => void;
}

interface VerticalNavigationProps {
  title: string;
  description: string;
  tags?: TagState[];
  buttons?: NavigationButton[];
  currentStep?: number;
  totalSteps?: number;
  showProgressBar?: boolean;
  showBack?: boolean;
  onMenuToggle?: (isOpen: boolean) => void;
  onBack?: () => void;
}

const VerticalNavigation: React.FC<VerticalNavigationProps> = ({
  title,
  description,
  tags,
  buttons,
  currentStep,
  totalSteps,
  showProgressBar = true,
  showBack = true,
  onMenuToggle,
  onBack,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState<ModalType>("methodology");

  const progressPercentage =
    currentStep && totalSteps ? (currentStep / totalSteps) * 100 : 0;

  const handleHamburgerClick = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    if (onMenuToggle) {
      onMenuToggle(newMenuState);
    }
    if (!isMenuOpen) {
      setSelectedModal("methodology");
    }
  };

  const handleMenuItemClick = (modalType: ModalType) => {
    setSelectedModal(modalType);
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    }
  };

  const menuItems = [
    { key: "methodology" as ModalType, label: "METHODOLOGY" },
    { key: "howToUse" as ModalType, label: "HOW TO USE THE TOOL" },
    { key: "privacy" as ModalType, label: "PRIVACY" },
    { key: "credits" as ModalType, label: "CREDITS" },
  ];

  return (
    <div
      className="verticalNavigation-container"
      style={{
        backgroundImage: `url(${verticalNavigationBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="verticalNavigation-overlay">
        <div className="verticalNavigation-content">
          {!isMenuOpen ? (
            <>
              <div className="verticalNavigation-top">
                <div
                  className="verticalNavigation-hamburger"
                  onClick={handleHamburgerClick}
                >
                  <div className="verticalNavigation-hamburger-line"></div>
                  <div className="verticalNavigation-hamburger-line"></div>
                  <div className="verticalNavigation-hamburger-line"></div>
                </div>

                <h2 className="verticalNavigation-title">{title}</h2>

                <p className="verticalNavigation-description">{description}</p>

                {buttons && buttons.length > 0 && (
                  <div className="verticalNavigation-buttons">
                    {buttons.map((button, index) => (
                      <button
                        key={index}
                        className="verticalNavigation-button"
                        onClick={button.onClick}
                      >
                        {button.label}
                      </button>
                    ))}
                  </div>
                )}

                {tags && tags.length > 0 && (
                  <div className="verticalNavigation-tags">
                    {tags.map((tag, index) => (
                      <div
                        key={index}
                        className={`verticalNavigation-tag ${
                          tag.checked ? "verticalNavigation-tag--checked" : ""
                        }`}
                      >
                        <span className="verticalNavigation-tag-icon">
                          {tag.checked ? "✓" : "•••"}
                        </span>
                        {tag.checked && tag.value
                          ? `${tag.label}: ${tag.value}`
                          : tag.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="verticalNavigation-bottom">
                {currentStep && totalSteps && showProgressBar && (
                  <div className="verticalNavigation-progress-container">
                    <div className="verticalNavigation-progress-bar">
                      <div
                        className="verticalNavigation-progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="verticalNavigation-progress-text">
                      {currentStep} of {totalSteps}
                    </span>
                  </div>
                )}

                {showBack && (
                  <button
                    className="verticalNavigation-back-button"
                    onClick={handleBackClick}
                  >
                    ← Back
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="verticalNavigation-menu-content">
              <div
                className="verticalNavigation-hamburger verticalNavigation-hamburger-open"
                onClick={handleHamburgerClick}
              >
                <div className="verticalNavigation-hamburger-line"></div>
                <div className="verticalNavigation-hamburger-line"></div>
                <div className="verticalNavigation-hamburger-line"></div>
              </div>

              <div className="verticalNavigation-menu-items">
                {menuItems.map((item) => (
                  <div
                    key={item.key}
                    className={`verticalNavigation-menu-item ${
                      selectedModal === item.key ? "active" : ""
                    }`}
                    onClick={() => handleMenuItemClick(item.key)}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="verticalNavigation-white-overlay">
        {isMenuOpen && (
          <Modal
            type={selectedModal}
            isOpen={true}
            onClose={() => {}}
            isInline={false}
            isOffsetModal={true}
            showCloseButton={false}
          />
        )}
      </div>
    </div>
  );
};

export default VerticalNavigation;
